import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, Tag } from "lucide-react";
import { Header, Footer, StickyCTA } from "../components/Layout";
import { articlesList } from "../data/blogArticles";

// Images pour les articles (mappées par catégorie)
const CATEGORY_IMAGES = {
  "Punaises de lit": "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80",
  "Dératisation": "/images/blog-rat.webp",
  "Guêpes/Frelons": "/images/blog-guepes.webp",
  "Désinfection": "https://images.unsplash.com/photo-1584744982491-665216d95f8b?w=800&q=80",
  "Désinsectisation": "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
  "Général": "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=800&q=80"
};

// Temps de lecture estimé par article
const READ_TIMES = {
  "punaises-lit-nice": "5 min",
  "deratisation-rats-06": "6 min",
  "nid-guepes-cannes": "4 min",
  "desinfection-covid-protocole": "7 min",
  "punaises-lit-paris-2025": "12 min",
  "deratisation-restaurant-nice-obligations": "10 min",
  "prix-deratisation-2025-tarifs": "11 min",
  "cafards-appartement-que-faire": "10 min",
  "comment-savoir-si-on-a-des-rats": "8 min",
  "traitement-punaises-lit-efficace": "12 min"
};

function Blog() {
  const [aiArticles, setAiArticles] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/articles/published`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setAiArticles(data))
      .catch(() => {});
  }, []);

  const allArticles = [
    ...aiArticles.map((a) => ({ id: a.id, title: a.title, date: a.date, category: a.category, excerpt: a.excerpt })),
    ...articlesList,
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <Helmet>
        <title>Blog Anti-Nuisibles : Conseils Dératisation & Désinsectisation 06 | Azur Tech</title>
        <meta name="description" content="Conseils d'experts anti-nuisibles : punaises de lit, rats, cafards, guêpes à Nice et dans les Alpes-Maritimes (06). Guides pratiques et tarifs par Azur Tech." />
        <link rel="canonical" href="https://azurtech06.fr/blog" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Blog Azur Tech
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Conseils et guides sur la désinsectisation, dératisation et désinfection à Nice et dans les Alpes-Maritimes
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {allArticles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.id}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all group"
                data-testid={`blog-article-${article.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={CATEGORY_IMAGES[article.category] || CATEGORY_IMAGES["Général"]}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center bg-emerald-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                      <Tag className="w-3 h-3 mr-1" />
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {article.date}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {READ_TIMES[article.id] || "5 min"}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-[#0f172a] mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 text-emerald-600 font-semibold text-sm group-hover:underline">
                    Lire la suite →
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-8 text-center text-white max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Besoin d'une intervention rapide ?
            </h2>
            <p className="text-lg text-emerald-100 mb-6">
              Nos experts sont disponibles 24h/24 - 7j/7 dans tout le 06 et l'Île-de-France
            </p>
            <a
              href="tel:0764079535"
              className="inline-block bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
            >
              Appeler Maintenant : 07 64 07 95 35
            </a>
          </div>
        </div>
      </div>

      <Footer />
      <StickyCTA />
    </div>
  );
}

export default Blog;
